import { Injectable, inject } from '@angular/core';
import {
  CheckoutOutcome,
  CheckoutPlan,
  CheckoutProvider,
  CheckoutService,
  PlanPrice,
} from './checkout.service';
import { IapCheckoutService } from './iap-checkout.service';
import { WebViewBridgeService } from '../webview/webview-bridge.service';
import { WebViewAction } from '../../enums/webview-action';

/**
 * Choisit le moyen de paiement à l'exécution.
 *
 * Dans l'app native, c'est l'achat intégré ; dans le navigateur, Stripe. Le
 * critère est la présence du canal natif, pas une variable de compilation :
 * la même version de l'app web sert le navigateur et la WebView.
 *
 * L'ancien binaire des magasins, qui ne connaît pas encore ce canal, ne vend
 * RIEN : y ouvrir Stripe ou l'essai maison enfreindrait les règles d'Apple
 * (3.1.1) et de Google (paiements). Le paywall y invite à mettre l'app à jour.
 */
@Injectable({ providedIn: 'root' })
export class CheckoutProviderService implements CheckoutProvider {
  private readonly stripe = inject(CheckoutService);
  private readonly iap = inject(IapCheckoutService);
  private readonly webViewBridge = inject(WebViewBridgeService);

  /** Le fournisseur en vigueur ici et maintenant. */
  get current(): CheckoutProvider {
    return this.iap.isAvailable() ? this.iap : this.stripe;
  }

  /** L'achat passe-t-il par le magasin ? Dicte les libellés de l'écran. */
  get isStore(): boolean {
    return this.iap.isAvailable();
  }

  /**
   * Dans l'app, mais un binaire antérieur à l'achat intégré. Les canaux
   * natifs qu'il expose déjà disent qu'on est dans la WebView.
   */
  get isOutdatedApp(): boolean {
    return (
      !this.iap.isAvailable() &&
      (this.webViewBridge.isChannelAvailable(WebViewAction.openExternalUrl) ||
        this.webViewBridge.isChannelAvailable(WebViewAction.openOAuthUrl))
    );
  }

  isAvailable(): boolean {
    return !this.isOutdatedApp && this.current.isAvailable();
  }

  getPrices(): Promise<PlanPrice[]> {
    return this.current.getPrices();
  }

  purchase(plan: CheckoutPlan): Promise<CheckoutOutcome> {
    if (this.isOutdatedApp) {
      return Promise.reject(new Error('Purchase refused: outdated store app'));
    }
    return this.current.purchase(plan);
  }

  manageBilling(): Promise<void> {
    return this.current.manageBilling();
  }

  /**
   * Restaure les achats du magasin. Sans objet sur le web, où il n'y a rien à
   * restaurer : l'abonnement Stripe suit le compte, pas l'appareil.
   */
  restore(): Promise<boolean> {
    return this.isStore ? this.iap.restore() : Promise.resolve(false);
  }

  /** Un achat vient-il d'être lancé hors de l'app ? (Stripe seulement) */
  consumePurchaseInFlight(): boolean {
    return this.stripe.consumePurchaseInFlight();
  }
}
